import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, PriceInput, VerifiedBadge } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import { formatMoney } from '@shared/lib/format';
import { useShortlist } from './shortlistStore';

const CreatorPreviewModal = lazy(() => import('./CreatorPreviewModal'));
const PriceCpmChart = lazy(() => import('./PriceCpmChart'));
import type { CpmRow } from './PriceCpmChart';

type Placement = 'reel' | 'story';

interface AnalysisRow {
  instagram_provider_id: string;
  handle: string;
  placement_type: Placement;
  providers_price: number | null;
  calculated_price: number | null;
  effective_price: number | null;
  suggested_price: number | null;
  median_reel_views: number | null;
  followers: number | null;
  account_er: number | null;
  distinct_brands: number | null;
  pct_branded: number | null;
}
interface RecRow {
  instagram_provider_id: string; handle: string;
  effective_price: number | null; suggested_price: number | null;
  value_ratio: number | null; followers: number | null; median_reel_views: number | null;
  account_er: number | null; distinct_brands: number | null; collab_with_shortlist?: boolean;
}
interface OverlapPair {
  a_provider_id: string; a_handle: string; b_provider_id: string; b_handle: string;
  collab_posts: number; shared_niches: number; band: 'high' | 'medium' | 'low';
}

// verdict from (entered price / fair worth). Kept in sync with the model's documented bands.
function verdict(ratio: number | null): { key: string; color: string; bg: string } {
  if (ratio == null || !isFinite(ratio)) return { key: 'na', color: '#9aa', bg: 'rgba(255,255,255,0.05)' };
  if (ratio < 0.6)  return { key: 'excellent', color: '#37d67a', bg: 'rgba(55,214,122,0.14)' };
  if (ratio < 0.95) return { key: 'good',      color: '#8bd450', bg: 'rgba(139,212,80,0.14)' };
  if (ratio < 1.35) return { key: 'fair',      color: '#e6c84a', bg: 'rgba(230,200,74,0.14)' };
  if (ratio < 2.0)  return { key: 'pricey',    color: '#e8965a', bg: 'rgba(232,150,90,0.14)' };
  return { key: 'overpriced', color: '#e8607a', bg: 'rgba(232,96,122,0.16)' };
}

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

// interpretive "why" chips from the measured factors (deterministic — no LLM needed here)
function driverKeys(g?: { views: number | null; followers: number | null; brands: number | null; branded: number | null }): { k: string; tone: 'good' | 'bad' }[] {
  if (!g) return [];
  const out: { k: string; tone: 'good' | 'bad' }[] = [];
  if (g.views != null) {
    if (g.views >= 300_000) out.push({ k: 'social.priceCheck.driver.bigReach', tone: 'good' });
    else if (g.views < 20_000) out.push({ k: 'social.priceCheck.driver.lowReach', tone: 'bad' });
  }
  if (g.brands != null) {
    if (g.brands >= 15) out.push({ k: 'social.priceCheck.driver.highDemand', tone: 'good' });
    else if (g.brands <= 3) out.push({ k: 'social.priceCheck.driver.lowDemand', tone: 'bad' });
  }
  if (g.views != null && g.followers && g.views / g.followers < 0.04)
    out.push({ k: 'social.priceCheck.driver.deadAudience', tone: 'bad' });
  if (g.branded != null && g.branded >= 0.4) out.push({ k: 'social.priceCheck.driver.adHeavy', tone: 'bad' });
  return out;
}

// overlap is reported PER PAIR (a real collab edge / shared-niche pair), never collapsed into
// a cluster — a chain A–B–C doesn't mean A and C share audience, so we show the actual edges.
const BAND_RANK: Record<string, number> = { low: 0, medium: 1, high: 2 };

const STORAGE_KEY = 'kgdb_price_check_v1';
function loadState(): { entered: Record<string, string>; analyzed: boolean } {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return { entered: (v && typeof v.entered === 'object' && v.entered) || {}, analyzed: !!(v && v.analyzed) };
  } catch { return { entered: {}, analyzed: false }; }
}

export default function PriceAnalysis() {
  const { t } = useTranslation('customer');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const { items, add, remove, clear, count } = useShortlist();
  const ids = useMemo(() => items.map((i) => i.providerId), [items]);

  // entered quoted prices, keyed `${providerId}:${placement}`
  const [entered, setEntered] = useState<Record<string, string>>(() => loadState().entered);
  const [analyzed, setAnalyzed] = useState(() => loadState().analyzed);
  const [saving, setSaving] = useState(false);
  const [recs, setRecs] = useState<RecRow[] | null>(null);
  const [overlap, setOverlap] = useState<OverlapPair[] | null>(null);
  const [viewer, setViewer] = useState<{ providerId: string; handle: string; followers: number; engagement: number } | null>(null);
  const openViewer = (providerId: string, handle: string, followers: number | null, engagement: number | null) =>
    setViewer({ providerId, handle, followers: followers ?? 0, engagement: engagement ?? 0 });
  const verif = useHandleVerification([...items.map((i) => i.handle), ...(recs ?? []).map((r) => r.handle)]);

  // persist inputs + analyzed state so a refresh restores the whole view (the submitted
  // prices are already in creator_price_submissions; this just keeps the on-screen state).
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ entered, analyzed })); } catch { /* quota */ }
  }, [entered, analyzed]);

  const analysisQ = useSupabaseQuery<AnalysisRow[]>(
    ['price-analysis', ids.join(',')],
    async () => (ids.length ? supabase.rpc('get_creator_price_analysis', { p_provider_ids: ids })
                            : ({ data: [], error: null } as never)),
  );
  const paramsQ = useSupabaseQuery<{ key: string; value: number }[]>(
    ['pricing-params'], async () => supabase.from('pricing_model_params').select('key, value'));
  const fairCpm = useMemo(() => Number((paramsQ.data ?? []).find((p) => p.key === 'fair_cpm')?.value ?? 0), [paramsQ.data]);

  // group rows by creator: { providerId: { meta, reel, story } }
  const byCreator = useMemo(() => {
    const m = new Map<string, { handle: string; followers: number | null; views: number | null;
      er: number | null; brands: number | null; branded: number | null; reel?: AnalysisRow; story?: AnalysisRow }>();
    for (const r of analysisQ.data ?? []) {
      const g = m.get(r.instagram_provider_id) ?? {
        handle: r.handle, followers: r.followers, views: r.median_reel_views,
        er: r.account_er, brands: r.distinct_brands, branded: r.pct_branded };
      g[r.placement_type] = r;
      m.set(r.instagram_provider_id, g);
    }
    return m;
  }, [analysisQ.data]);

  const val = (pid: string, pl: Placement) => entered[`${pid}:${pl}`] ?? '';
  const num = (pid: string, pl: Placement) => { const n = Number(val(pid, pl)); return isFinite(n) && n > 0 ? n : null; };

  // best-deal ranking across the shortlist (reel), for the comparison strip
  const ranked = useMemo(() => {
    const rows = items.map((it) => {
      const g = byCreator.get(it.providerId);
      const raw = entered[`${it.providerId}:reel`];
      const nr = raw ? Number(raw) : NaN;
      const sug = g?.reel?.suggested_price ?? null;
      const ratio = isFinite(nr) && nr > 0 && sug ? nr / sug : null;
      return { providerId: it.providerId, handle: it.handle, ratio };
    }).filter((x): x is { providerId: string; handle: string; ratio: number } => x.ratio != null);
    return rows.sort((a, b) => a.ratio - b.ratio);
  }, [items, byCreator, entered]);

  // after a refresh (analyzed restored from storage) the recommendations aren't in state — refetch them
  useEffect(() => {
    if (!analyzed || recs || !analysisQ.data || ids.length === 0) return;
    const reelPrices = items
      .map((i) => { const raw = entered[`${i.providerId}:reel`]; const nr = raw ? Number(raw) : NaN; return isFinite(nr) && nr > 0 ? nr : null; })
      .filter((n): n is number => n != null);
    if (!reelPrices.length) return;
    const lo = Math.min(...reelPrices) * 0.7, hi = Math.max(...reelPrices) * 1.3;
    supabase.rpc('get_price_recommendations', { p_placement: 'reel', p_min: Math.round(lo), p_max: Math.round(hi), p_exclude: ids, p_limit: 5 })
      .then(({ data }) => setRecs((data as RecRow[]) ?? []));
    supabase.rpc('get_shortlist_overlap', { p_provider_ids: ids })
      .then(({ data }) => setOverlap((data as OverlapPair[]) ?? []));
  }, [analyzed, recs, analysisQ.data, ids, items, entered]);

  async function analyze() {
    setSaving(true);
    // 1) record every entered quote (learning loop) — best-effort
    const rows: { instagram_provider_id: string; placement_type: Placement; input_price: number }[] = [];
    for (const it of items) for (const pl of ['reel', 'story'] as Placement[]) {
      const n = num(it.providerId, pl);
      if (n) rows.push({ instagram_provider_id: it.providerId, placement_type: pl, input_price: n });
    }
    if (rows.length) await supabase.from('creator_price_submissions').insert(rows);

    // 2) recommendations in the entered reel-price range (best value, excluding the shortlist)
    const reelPrices = items.map((i) => num(i.providerId, 'reel')).filter((n): n is number => n != null);
    if (reelPrices.length) {
      const lo = Math.min(...reelPrices) * 0.7, hi = Math.max(...reelPrices) * 1.3;
      const { data } = await supabase.rpc('get_price_recommendations',
        { p_placement: 'reel', p_min: Math.round(lo), p_max: Math.round(hi), p_exclude: ids, p_limit: 5 });
      setRecs((data as RecRow[]) ?? []);
    }

    // 3) audience-overlap check across the shortlist (collab graph)
    const { data: ov } = await supabase.rpc('get_shortlist_overlap', { p_provider_ids: ids });
    setOverlap((ov as OverlapPair[]) ?? []);

    setAnalyzed(true);
    setSaving(false);
  }

  if (count === 0) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 16px', textAlign: 'center', color: 'var(--muted)' }}>
        <NamedIcon name="money" size={40} color="#556" />
        <h2 style={{ color: 'var(--text)', margin: '16px 0 8px' }}>{tr('social.priceCheck.listTitle', 'Мой список')}</h2>
        <p>{tr('social.priceCheck.empty', 'Добавьте блогеров в анализ (кнопка «＋ в анализ» на карточке), укажите цену, которую вам назвали, и мы оценим, справедлива ли она.')}</p>
        {/* the button lives on the creator card in the catalog — give the way there */}
        <Link to="/customer/creators" className="btn" style={{ display: 'inline-block', marginTop: 12, padding: '10px 18px', borderRadius: 10, background: 'var(--primary)', color: '#fff', textDecoration: 'none', fontWeight: 600 }}>
          {tr('social.priceCheck.toCatalog', 'Выбрать блогеров в каталоге')}
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 940, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        {/* Named like the menu item («Мой список»); price check is what you do here. */}
        <h2 style={{ color: 'var(--text)', margin: 0 }}>{tr('social.priceCheck.listTitle', 'Мой список')} <span style={{ color: 'var(--dim)', fontWeight: 400 }}>· {count}/10</span></h2>
        <button type="button" onClick={() => { clear(); setEntered({}); setAnalyzed(false); setRecs(null); setOverlap(null); }} style={ghost}>{tr('social.priceCheck.clear', 'Очистить')}</button>
      </div>
      <p style={{ color: 'var(--dim)', fontSize: 13, marginTop: 0 }}>
        {tr('social.priceCheck.hint', 'Впишите цену, которую вам назвали, и нажмите «Проанализировать». Вердикт сравнивает вашу цену с честной ценностью блогера (охват, вовлечённость и спрос брендов).')}
      </p>

      {analysisQ.isLoading ? (
        <div style={{ display: 'grid', gap: 12 }}>{Array.from({ length: count }).map((_, i) => <Skeleton key={i} height={120} />)}</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((it) => {
            const g = byCreator.get(it.providerId);
            return (
              <div key={it.providerId} style={card}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* identity + stats + why-chips */}
                  <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                    <span onClick={() => openViewer(it.providerId, it.handle, g?.followers ?? it.followers ?? null, g?.er ?? null)}
                       style={{ color: 'var(--accent-text)', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>@{it.handle}</span>
                    <VerifiedBadge {...verif(it.handle)} size={14} />
                    {/* Words, not bare icons: «29K 29K 2.1% 52%» didn't say which number was which. */}
                    <div style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap', marginTop: 6, color: 'var(--muted)', fontSize: 12 }}>
                      <span>{tr('social.priceCheck.s.followers', '{{v}} подписчиков', { v: fmtK(g?.followers ?? it.followers) })}</span>
                      <span>{tr('social.priceCheck.s.views', '{{v}} просмотров Reels', { v: fmtK(g?.views) })}</span>
                      <span>ER {g?.er != null ? `${Number(g.er).toFixed(1)}%` : '—'}</span>
                      <span>{tr('social.priceCheck.s.brands', '{{v}} брендов', { v: g?.brands ?? 0 })}</span>
                      <span>{tr('social.priceCheck.s.adShare', 'реклама в {{v}} постов', { v: g?.branded != null ? `${Math.round(Number(g.branded) * 100)}%` : '—' })}</span>
                    </div>
                    {analyzed && driverKeys(g).length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {driverKeys(g).map((d) => (
                          <span key={d.k} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20,
                            color: d.tone === 'good' ? '#8bd450' : '#e8965a',
                            background: d.tone === 'good' ? 'rgba(139,212,80,0.12)' : 'rgba(232,150,90,0.12)' }}>
                            {tr(d.k, d.k)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* price inputs on the handle row + remove */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flex: '1 1 300px', minWidth: 0 }}>
                    {(['reel', 'story'] as Placement[]).map((pl) => {
                      const row = g?.[pl];
                      const suggested = row?.suggested_price ?? null;
                      const n = num(it.providerId, pl);
                      const ratio = n && suggested ? n / suggested : null;
                      const v = verdict(ratio);
                      return (
                        <div key={pl} style={{ flex: '1 1 0', minWidth: 0, maxWidth: 200 }}>
                          <div style={{ color: 'var(--dim)', fontSize: 12, marginBottom: 6 }}>{pl === 'reel' ? tr('social.priceCheck.reel', 'Рилс') : tr('social.priceCheck.story', 'Сторис')}</div>
                          <PriceInput value={val(it.providerId, pl)}
                            onChange={(v) => setEntered((s) => ({ ...s, [`${it.providerId}:${pl}`]: v }))}
                            placeholder={tr('social.priceCheck.yourPrice', 'Ваша цена, сом')}
                            style={{ ...input, width: '100%', boxSizing: 'border-box' }} />
                          {row?.providers_price ? (
                            <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                              {tr('social.priceCheck.listPrice', 'прайс блогера {{v}} сом', { v: Math.round(Number(row.providers_price)).toLocaleString('ru-RU') })}
                            </div>
                          ) : null}
                          {analyzed && (
                            <div style={{ marginTop: 8 }}>
                              <span style={{ ...pill, color: v.color, background: v.bg }}>{tr(`social.priceCheck.verdict.${v.key}`, v.key)}</span>
                              {ratio != null && (
                                <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: ratio < 0.92 ? '#8bd450' : ratio > 1.08 ? '#e8965a' : '#c9c96a' }}>
                                  {ratio < 0.92 ? tr('social.priceCheck.betterX', 'в {{x}}× выгоднее', { x: (1 / ratio).toFixed(1) })
                                    : ratio > 1.08 ? tr('social.priceCheck.worseX', 'в {{x}}× дороже', { x: ratio.toFixed(1) })
                                    : tr('social.priceCheck.atFair', 'по справедливой цене')}
                                </div>
                              )}
                              {pl === 'reel' && n && g?.views ? (
                                <div style={{ color: 'var(--dim)', fontSize: 10.5, marginTop: 4 }}>
                                  {tr('social.priceCheck.cpm', 'CPM {{you}} · рынок ~{{market}}', { you: Math.round((n / g.views) * 1000), market: Math.round(fairCpm) })}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button type="button" onClick={() => remove(it.providerId)} style={{ ...ghost, padding: '2px 8px' }}><NamedIcon name="x" size={12} /></button>
                  </div>
                </div>
                {analyzed && g && (() => {
                  const nr = num(it.providerId, 'reel');
                  const sug = g.reel?.suggested_price ?? null;
                  const ratio = nr && sug ? nr / sug : null;
                  if (!ratio || !nr) return null;
                  const key = ratio < 0.6 ? 'excellent' : ratio < 0.95 ? 'good' : ratio < 1.35 ? 'fair' : ratio < 2 ? 'pricey' : 'overpriced';
                  const cpm = g.views ? Math.round((nr / g.views) * 1000) : null;
                  const drivers = driverKeys(g);
                  return (
                    <p style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--muted)' }}>
                      <b style={{ color: verdict(ratio).color }}>{tr(`social.priceCheck.say.${key}`, key)}.</b>{' '}
                      {tr('social.priceCheck.sayReach', 'охват ~{{v}} за {{p}} сом (CPM {{c}} против рынка ~{{m}})',
                        { v: fmtK(g.views), p: nr.toLocaleString('ru-RU'), c: cpm ?? '—', m: Math.round(fairCpm) })}
                      {drivers.length ? ' — ' + drivers.map((d) => tr(d.k, d.k)).join(', ') + '.' : '.'}
                    </p>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      <button type="button" onClick={analyze} disabled={saving} style={{ ...primary, marginTop: 16, opacity: saving ? 0.6 : 1 }}>
        {saving ? '…' : tr('social.priceCheck.analyze', 'Проанализировать')}
      </button>

      {analyzed && overlap && overlap.length > 0 && (() => {
        const tone = (band: string) => band === 'high' ? { c: '#e8607a', bg: 'rgba(232,96,122,0.10)', b: 'rgba(232,96,122,0.35)' }
          : band === 'medium' ? { c: '#e8965a', bg: 'rgba(232,150,90,0.10)', b: 'rgba(232,150,90,0.32)' }
          : { c: '#c9c96a', bg: 'rgba(201,201,106,0.10)', b: 'rgba(201,201,106,0.28)' };
        const pairs = [...overlap].sort((a, b) => BAND_RANK[b.band] - BAND_RANK[a.band] || b.collab_posts - a.collab_posts);
        return (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ color: 'var(--text)', fontSize: 15, marginBottom: 4 }}>{tr('social.priceCheck.overlap.title', 'Пересечение аудитории')}</h3>
            <p style={{ color: 'var(--dim)', fontSize: 12, marginTop: 0, marginBottom: 8 }}>
              {tr('social.priceCheck.overlap.subtitle', 'Пары блогеров с общей аудиторией — берёте обоих, охват пары меньше суммы:')}
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {pairs.map((p, i) => {
                const to = tone(p.band);
                return (
                  <div key={i} style={{ border: `1px solid ${to.b}`, background: to.bg, borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ color: to.c, fontSize: 12.5, fontWeight: 600, marginBottom: 3 }}>
                      {p.band === 'high' ? tr('social.priceCheck.overlap.high', 'Сильное пересечение')
                        : p.band === 'medium' ? tr('social.priceCheck.overlap.medium', 'Вероятное пересечение')
                        : tr('social.priceCheck.overlap.low', 'Возможное пересечение')}
                      {' · '}@{p.a_handle} ↔ @{p.b_handle}
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.5 }}>
                      {p.band === 'low'
                        ? tr('social.priceCheck.overlap.sayNiche', 'Одна ниша и язык — аудитории могут пересекаться. Совместный охват может быть меньше суммы.')
                        : tr('social.priceCheck.overlap.sayPair', 'Совместных постов: {{n}}. Делают контент вместе — их подписчики во многом общие, реальный охват этой пары меньше суммы двух.',
                            { n: p.collab_posts })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ color: 'var(--dim)', fontSize: 11.5, margin: '6px 0 0' }}>
              {tr('social.priceCheck.overlap.hint', 'Для максимального уникального охвата берите блогеров, которые НЕ сотрудничают между собой и работают в разных нишах.')}
            </p>
          </div>
        );
      })()}

      {analyzed && ranked.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ color: 'var(--text)', fontSize: 15, marginBottom: 8 }}>{tr('social.priceCheck.compare', 'Сравнение — от лучшей сделки к худшей')}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--dim)' }}>
                  <th style={thc}>{tr('social.priceCheck.tbl.creator', 'Блогер')}</th>
                  <th style={thcR}>{tr('social.priceCheck.followers', 'Подписчики')}</th>
                  <th style={thcR}>{tr('social.priceCheck.tbl.reach', 'Охват')}</th>
                  <th style={thcR}>ER</th>
                  <th style={thcR}>{tr('social.priceCheck.tbl.brands', 'Бренды')}</th>
                  <th style={thcR}>{tr('social.priceCheck.tbl.yourPrice', 'Ваша цена')}</th>
                  <th style={thcR}>CPM</th>
                  <th style={thcR}>{tr('social.priceCheck.tbl.verdict', 'Вердикт')}</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((r) => {
                  const g = byCreator.get(r.providerId);
                  const nr = num(r.providerId, 'reel');
                  const cpm = g?.views && nr ? Math.round((nr / g.views) * 1000) : null;
                  const vv = verdict(r.ratio);
                  return (
                    <tr key={r.providerId} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={tdc}><span onClick={() => openViewer(r.providerId, r.handle, g?.followers ?? null, g?.er ?? null)} style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>@{r.handle}</span> <VerifiedBadge {...verif(r.handle)} size={14} /></td>
                      <td style={tdcR}>{fmtK(g?.followers)}</td>
                      <td style={tdcR}>{fmtK(g?.views)}</td>
                      <td style={tdcR}>{g?.er != null ? `${Number(g.er).toFixed(1)}%` : '—'}</td>
                      <td style={tdcR}>{g?.brands ?? 0}</td>
                      <td style={tdcR}>{nr ? nr.toLocaleString('ru-RU') : '—'}</td>
                      <td style={tdcR}>{cpm ?? '—'}</td>
                      <td style={tdcR}>
                        <span style={{ ...pill, color: vv.color, background: vv.bg, fontSize: 10.5 }}>{tr(`social.priceCheck.verdict.${vv.key}`, vv.key)}</span>
                        <span style={{ display: 'block', color: vv.color, fontSize: 10, marginTop: 2 }}>
                          {r.ratio < 0.92 ? tr('social.priceCheck.betterX', 'в {{x}}× выгоднее', { x: (1 / r.ratio).toFixed(1) })
                            : r.ratio > 1.08 ? tr('social.priceCheck.worseX', 'в {{x}}× дороже', { x: r.ratio.toFixed(1) })
                            : tr('social.priceCheck.atFair', 'по справедливой цене')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analyzed && ranked.length > 0 && (() => {
        const cpmRows = ranked.map((r) => {
          const g = byCreator.get(r.providerId);
          const nr = num(r.providerId, 'reel');
          if (!g?.views || !nr) return null;
          return { handle: `@${r.handle}`, cpm: Math.round((nr / g.views) * 1000), color: verdict(r.ratio).color };
        }).filter((x): x is CpmRow => x != null);
        if (!cpmRows.length) return null;
        return (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ color: 'var(--text)', fontSize: 15, marginBottom: 4 }}>{tr('social.priceCheck.cpmChart', 'CPM против рынка — цена за 1000 просмотров')}</h3>
            <Suspense fallback={<Skeleton height={160} />}>
              <PriceCpmChart rows={cpmRows} market={Math.round(fairCpm)}
                marketLabel={tr('social.priceCheck.cpmMarket', 'рынок ~{{m}}', { m: Math.round(fairCpm) })} />
            </Suspense>
            <p style={{ color: 'var(--dim)', fontSize: 11.5, margin: '2px 0 0' }}>
              {tr('social.priceCheck.cpmChartHint', 'Короче полоса — дешевле охват. Левее пунктирной линии — дешевле рынка.')}
            </p>
          </div>
        );
      })()}

      {analyzed && recs && recs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ color: 'var(--text)', fontSize: 15 }}>{tr('social.priceCheck.recs', 'Похожие по цене — лучшее соотношение')}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {recs.map((r) => {
              const v = verdict(r.value_ratio);
              return (
                <div key={r.instagram_provider_id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span onClick={() => openViewer(r.instagram_provider_id, r.handle, r.followers, r.account_er)} style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>@{r.handle}</span>
                    <VerifiedBadge {...verif(r.handle)} size={14} />
                    {r.collab_with_shortlist && (
                      <span title={tr('social.priceCheck.recOverlapHint', 'Сотрудничает с блогерами из вашего списка — аудитория пересекается')}
                        style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, color: '#e8965a', background: 'rgba(232,150,90,0.12)' }}>
                        {tr('social.priceCheck.recOverlap', 'пересечение')}
                      </span>
                    )}
                  </span>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: 'var(--muted)', fontSize: 12, flexWrap: 'wrap' }}>
                    <span><NamedIcon name="workers" size={11} /> {fmtK(r.followers)}</span>
                    <span><NamedIcon name="eye" size={11} /> {fmtK(r.median_reel_views)}</span>
                    <span><NamedIcon name="heart" size={11} /> {r.account_er != null ? `${Number(r.account_er).toFixed(1)}%` : '—'}</span>
                    <span><NamedIcon name="handshake" size={11} /> {r.distinct_brands ?? 0}</span>
                    <span style={{ color: 'var(--text-2)' }}>{r.effective_price ? formatMoney(r.effective_price) : '—'}</span>
                    <span style={{ ...pill, color: v.color, background: v.bg }}>{tr(`social.priceCheck.verdict.${v.key}`, v.key)}</span>
                    <button type="button" onClick={() => add({ providerId: r.instagram_provider_id, handle: r.handle, followers: r.followers })}
                      style={{ ...ghost, padding: '2px 9px', color: 'var(--accent)', borderColor: 'rgba(74,158,255,0.4)' }}>{tr('social.creator.addAnalysis', '＋ в анализ')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewer && (
        <Suspense fallback={null}>
          <CreatorPreviewModal
            handle={viewer.handle}
            instagramProviderId={viewer.providerId}
            followers={viewer.followers}
            engagement={viewer.engagement}
            onClose={() => setViewer(null)}
            onOpenCreator={(p) => openViewer(p.providerId, p.handle, p.followers ?? null, null)}
          />
        </Suspense>
      )}
    </div>
  );
}

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 14 };
const thc: React.CSSProperties = { padding: '6px 8px', fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'left' };
const thcR: React.CSSProperties = { ...thc, textAlign: 'right' };
const tdc: React.CSSProperties = { padding: '7px 8px', color: '#cdd', whiteSpace: 'nowrap' };
const tdcR: React.CSSProperties = { ...tdc, textAlign: 'right' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 6, background: 'rgba(0,0,0,0.25)', color: '#eee', border: '1px solid rgba(74,158,255,0.25)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' };
const pill: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20 };
const primary: React.CSSProperties = { background: 'rgba(74,158,255,0.18)', border: '1px solid rgba(74,158,255,0.5)', color: '#bcd0ff', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 };
const ghost: React.CSSProperties = { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#99a', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 };
